import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

import type { WebSocketConfig } from '@/interfaces/WebSocketInterfaces';

// Make Pusher available globally for Echo
declare global {
  interface Window {
    Pusher: typeof Pusher;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Echo?: Echo<any>;
  }
}

window.Pusher = Pusher;

// Get auth token from localStorage or Redux store
const getAuthToken = (): string | null => {
  // Try localStorage first
  const token = localStorage.getItem('auth_token');
  if (token) {
    return token;
  }

  // Try to get from Redux store if available
  try {
    const storeState = localStorage.getItem('persist:auth');
    if (storeState) {
      const authState = JSON.parse(storeState);
      const { tokens } = authState ? JSON.parse(authState.tokens) : null;
      return tokens?.access_token || null;
    }
  } catch (error) {
    console.error('Error retrieving auth token:', error);
  }

  return null;
};

// WebSocket configuration
export const webSocketConfig: WebSocketConfig = {
  host: import.meta.env.VITE_REVERB_HOST || 'localhost',
  port: parseInt(import.meta.env.VITE_REVERB_PORT || '443'),
  scheme: (import.meta.env.VITE_REVERB_SCHEME || 'https') === 'https' ? 'wss' : 'ws',
  appKey: import.meta.env.VITE_REVERB_APP_KEY || '',
  authEndpoint: '/api/v1/broadcasting/auth',
  enabledTransports: ['ws', 'wss'],
  forceTLS: (import.meta.env.VITE_REVERB_SCHEME || 'https') === 'https',
  reconnectDelay: 5000,
  maxReconnectAttempts: 10,
};

// Create Echo instance factory
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createEchoInstance = (token?: string | null): Echo<any> | null => {
  const authToken = token || getAuthToken();

  if (!webSocketConfig.appKey) {
    console.warn('WebSocket app key not configured');
    return null;
  }

  try {
    const echo = new Echo({
      broadcaster: 'reverb',
      key: webSocketConfig.appKey,
      wsHost: webSocketConfig.host,
      wsPort: webSocketConfig.port,
      wssPort: webSocketConfig.port,
      forceTLS: webSocketConfig.forceTLS,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      enabledTransports: webSocketConfig.enabledTransports as any,
      authEndpoint: webSocketConfig.authEndpoint,
      auth: authToken
        ? {
            headers: {
              Authorization: `Bearer ${authToken}`,
              Accept: 'application/json',
            },
          }
        : undefined,
      // Additional options
      encrypted: webSocketConfig.forceTLS,
      disableStats: webSocketConfig.disableStats ?? true,
      enableLogging: import.meta.env.DEV,
    });

    // Store instance globally for debugging in development
    if (import.meta.env.DEV) {
      window.Echo = echo;
    }

    return echo;
  } catch (error) {
    console.error('Failed to create Echo instance:', error);
    return null;
  }
};

// Get or create Echo instance
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let echoInstance: Echo<any> | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getEchoInstance = (): Echo<any> | null => {
  if (!echoInstance) {
    echoInstance = createEchoInstance();
  }
  return echoInstance;
};

// Update Echo auth token
export const updateEchoAuthToken = (token: string | null): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (echoInstance && (echoInstance as any).connector) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { connector } = echoInstance as any;
    if (token) {
      connector.options.auth = {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      };
    } else {
      connector.options.auth = undefined;
    }
  }
};

// Disconnect Echo instance
export const disconnectEcho = (): void => {
  if (echoInstance) {
    echoInstance.disconnect();
    echoInstance = null;
  }
};

// Reconnect Echo instance
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const reconnectEcho = (token?: string | null): Echo<any> | null => {
  disconnectEcho();
  echoInstance = createEchoInstance(token);
  return echoInstance;
};

// Subscribe to user's private channel
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const subscribeToUserChannel = (userId: string): any => {
  if (!echoInstance) {
    console.error('Echo instance not initialized');
    return null;
  }

  return echoInstance.private(`user.${userId}`);
};

// Subscribe to company's private channel
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const subscribeToCompanyChannel = (companySlug: string): any => {
  if (!echoInstance) {
    console.error('Echo instance not initialized');
    return null;
  }

  return echoInstance.private(`company.${companySlug}`);
};

// Subscribe to delivery channel
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const subscribeToDeliveryChannel = (deliveryId: string): any => {
  if (!echoInstance) {
    console.error('Echo instance not initialized');
    return null;
  }

  return echoInstance.private(`delivery.${deliveryId}`);
};

// Helper to listen for common events on a channel
export const listenToChannelEvents = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  channel: any,
  handlers: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onNotification?: (data: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onDeliveryUpdate?: (data: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onAnnouncement?: (data: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (error: any) => void;
  },
): void => {
  if (!channel) {
    return;
  }

  // Notification events
  if (handlers.onNotification) {
    channel.listen('.notification.received', handlers.onNotification);
  }

  // Delivery updates
  if (handlers.onDeliveryUpdate) {
    channel.listen('.delivery.status.updated', handlers.onDeliveryUpdate);
    channel.listen('.delivery.location.updated', handlers.onDeliveryUpdate);
    channel.listen('.delivery.assigned', handlers.onDeliveryUpdate);
  }

  // Company announcements
  if (handlers.onAnnouncement) {
    channel.listen('.company.announcement', handlers.onAnnouncement);
  }

  // Error handling
  if (handlers.onError) {
    channel.error(handlers.onError);
  }
};

// Remove all listeners from a channel
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeChannelListeners = (channel: any): void => {
  if (!channel) {
    return;
  }

  channel.stopListening('.notification.received');
  channel.stopListening('.delivery.status.updated');
  channel.stopListening('.delivery.location.updated');
  channel.stopListening('.delivery.assigned');
  channel.stopListening('.company.announcement');
};
